import { formatName } from "@api";
import type { ITeam } from "@bbj-types/team";
import type { IUser } from "@bbj-types/user";
import {
  Autocomplete,
  type AutocompleteProps,
  type AutocompleteRenderValue,
  type AutocompleteRenderValueGetItemProps,
  Typography,
} from "@mui/material";
import { useGlobalContext } from "@root/context";
import type React from "react";
import { useCallback } from "react";
import { AutocompleteAuthorsChip, chipClasses } from "./styles";
import { getOptionLabel, isOptionEqualToValue } from "./utils";

export type TAutocompleteAuthorsValue = Pick<IUser, "_id" | "firstName" | "lastName" | "isDisabled"> & {
  teams?: Pick<ITeam, "_id" | "name">[];
};

export type TAutocompleteAuthorsBaseProps<
  Value extends TAutocompleteAuthorsValue,
  Multiple extends boolean,
  DisableClearable extends boolean | undefined,
  ChipComponent extends React.ElementType = "div",
> = Omit<
  AutocompleteProps<Value, Multiple, DisableClearable, false, ChipComponent>,
  "renderValue" | "getOptionLabel" | "isOptionEqualToValue"
> & {
  teams?: boolean;
};
export const AutocompleteAuthorsBase = <
  Value extends TAutocompleteAuthorsValue,
  Multiple extends boolean,
  DisableClearable extends boolean | undefined,
  ChipComponent extends React.ElementType = "div",
>({
  limitTags = 1,
  teams,
  ...props
}: TAutocompleteAuthorsBaseProps<Value, Multiple, DisableClearable, ChipComponent>) => {
  const { user } = useGlobalContext();

  const renderValue = useCallback(
    (
      value: AutocompleteRenderValue<Value, Multiple, false>,
      getTagProps: AutocompleteRenderValueGetItemProps<Multiple>
    ) => {
      const values = Array.isArray(value) ? value : value ? [value] : [];
      return values.map((author, index) => (
        <AutocompleteAuthorsChip
          color="secondary"
          label={
            <Typography data-hj-suppress variant="body1" noWrap>
              {user._id === author._id ? "Moi" : formatName(author)}
            </Typography>
          }
          size="small"
          {...getTagProps({ index })}
          className={chipClasses.root}
          key={author._id}
        />
      ));
    },
    [user]
  );

  return (
    <Autocomplete
      filterOptions={(x) => x}
      getOptionLabel={getOptionLabel}
      getOptionKey={(o) => o._id}
      isOptionEqualToValue={isOptionEqualToValue}
      renderValue={renderValue}
      limitTags={limitTags}
      disableCloseOnSelect
      {...((teams ?? user.organisation.features.team.active)
        ? { groupBy: (o: Value) => (o.teams?.[0] ? o.teams[0].name : "Sans équipe") }
        : undefined)}
      {...props}
      renderOption={(props, author) => (
        <li {...props} key={`${author._id}${author.teams?.[0]?._id ?? ""}`} data-hj-suppress="true">
          {author.firstName} {author.lastName}
        </li>
      )}
    />
  );
};
